import { MutableRefObject, useEffect, useRef, useState } from 'react';

/*
 ** This is not as robust because it is not considering
 ** the differences between `mouseover`, `pointerover`, `touchstart`
 ** https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/interactions/src/useHover.ts
 */
export function useHover<T extends HTMLElement>(): [MutableRefObject<T | null>, boolean] {
  const [value, setValue] = useState<boolean>(false);
  const ref = useRef<T | null>(null);

  const handleMouseOver = () => setValue(true);
  const handleMouseOut = () => setValue(false);

  useEffect(() => {
    const node = ref.current;
    if (node) {
      node.addEventListener('mouseover', handleMouseOver);
      node.addEventListener('mouseout', handleMouseOut);
      return () => {
        node.removeEventListener('mouseover', handleMouseOver);
        node.removeEventListener('mouseout', handleMouseOut);
      };
    }
    return undefined;
  }, []);

  return [ref, value];
}

/**
 * Reactstrap is applying role=document to the wrong element in the modal in terms of accessibility best practices.
 * Outer .modal has role="dialog" which is good but  the Inner .modal-dialog has role="document" - this becomes problematic for screen readers.
 * This role="document" causes screen readers to re-announce the modal title and/or content causing duplicate readings.
 * This hook removes role attribute from modal-dialog element when modal is open.
 * @param isOpen - boolean
 * @param modalSelector - string
 */
export const useRemoveDocumentRole = (isOpen: boolean, modalSelector = '.modal-dialog[role="document"]'): void => {
  useEffect(() => {
    // If modal isn't open, do nothing
    if (!isOpen) return undefined;

    // Remove the role(document) from modal dialog currently opened in the DOM.
    const removeRole = () => {
      document.querySelectorAll(modalSelector).forEach((el) => el.removeAttribute('role'));
    };

    // Debounce/coalesce multiple mutation callbacks into one microtask so we don't run `querySelectorAll` repeatedly in the same frame.
    let scheduled = false;

    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;

      // Use a microtask to batch up bursts. `void` silences no-floating-promises.
      void Promise.resolve().then(() => {
        scheduled = false;
        removeRole();
      });
    });

    // Observe the whole body subtree *only while the modal is open*. This is the most reliable for Reactstrap portals/animations that add `.modal-dialog` after `.modal.show` appears.
    observer.observe(document.body, {
      attributes: true, // react when attributes (like `role`) change
      subtree: true, // include descendants (needed for portal inserts)
    });

    // Handle the case where the dialog is already in the DOM when we start.
    removeRole();

    // Cleanup: stop observing as soon as the modal closes / effect re-runs.
    return () => observer.disconnect();
  }, [isOpen, modalSelector]);
};
